import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import { useNotify, usePermissions, useRecordContext, useRefresh } from "react-admin";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage } from "../shared/api-error";
import { MADRASA_FEES_VOID } from "../shared/madrasa-permissions";
import type { ReceiptRecord } from "./FeeReceiptSheet";

/** Voids a receipt entered by mistake. The reason is required, and the receipt keeps its number. */
export function VoidReceiptButton() {
  const record = useRecordContext<ReceiptRecord>();
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!record || record.voided || !hasPermission(permissions, MADRASA_FEES_VOID)) return null;

  const close = () => {
    if (busy) return;
    setOpen(false);
    setReason("");
  };

  const submit = async () => {
    setBusy(true);
    try {
      await callApi({
        location: `feeReceipts/${record.id}/void`,
        method: "POST",
        data: { reason: reason.trim() },
      });
      notify("Receipt voided. The dues it paid are outstanding again.", { type: "success" });
      setOpen(false);
      setReason("");
      refresh();
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not void the receipt"), { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button color="error" onClick={() => setOpen(true)}>
        Void receipt
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
        <DialogTitle>Void receipt {record.receiptNo}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Use this for a receipt entered by mistake. The dues it paid become outstanding again.
            The receipt is kept, marked VOID, and its number is never reused. This cannot be undone.
          </DialogContentText>
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            autoFocus
            helperText="For example: entered against the wrong student."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            color="error"
            variant="contained"
            disabled={busy || reason.trim().length < 3}
          >
            {busy ? "Voiding…" : "Void receipt"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
